import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { INSURANCE_MENU, PHONE_TEL } from "../lib/site";

export const Logo = ({ dark = false }) => (
  <Link to="/" className="flex items-center" data-testid="logo-link">
    {dark ? (
      <span className="inline-flex rounded-2xl bg-white px-4 py-3 shadow-soft">
        <img
          src="/images/logo.webp"
          alt="Pocket Bima Insurance Service — Chhoti Pocket, Badi Suraksha"
          width="800"
          height="667"
          className="h-20 w-auto"
          data-testid="brand-logo"
        />
      </span>
    ) : (
      <img
        src="/images/logo.webp"
        alt="Pocket Bima Insurance Service — Chhoti Pocket, Badi Suraksha"
        width="800"
        height="667"
        className="h-14 w-auto"
        data-testid="brand-logo"
      />
    )}
  </Link>
);

export default function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const linkCls = ({ isActive }) =>
    `text-sm font-semibold transition-colors duration-200 hover:text-mint-600 ${
      isActive ? "text-mint-600" : "text-navy"
    }`;

  return (
    <header
      className="sticky top-0 z-50 border-b border-navy/10 bg-white/80 backdrop-blur-xl"
      data-testid="site-header"
    >
      <div className="mx-auto flex h-[76px] max-w-7xl items-center justify-between px-5 sm:px-8 md:px-12">
        <Logo />

        <nav className="hidden items-center gap-8 lg:flex" data-testid="desktop-nav">
          <NavLink to="/" end className={linkCls}>Home</NavLink>
          <NavLink to="/about" className={linkCls}>About Us</NavLink>
          <div className="group relative">
            <button
              className={`flex items-center gap-1 text-sm font-semibold transition-colors duration-200 group-hover:text-mint-600 ${
                location.pathname.includes("insurance") ? "text-mint-600" : "text-navy"
              }`}
              data-testid="insurance-menu-button"
            >
              Insurance <ChevronDown className="h-3.5 w-3.5 transition-transform duration-200 group-hover:rotate-180" />
            </button>
            <div className="invisible absolute left-1/2 top-full z-50 w-64 -translate-x-1/2 pt-3 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100">
              <div className="rounded-2xl border border-navy/10 bg-white p-2 shadow-lift">
                {INSURANCE_MENU.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="block rounded-xl px-4 py-2.5 text-sm font-medium text-charcoal transition-colors duration-150 hover:bg-mint-50 hover:text-navy"
                    data-testid={`nav-${item.to.slice(1)}`}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
          <Link to="/#why" className="text-sm font-semibold text-navy transition-colors duration-200 hover:text-mint-600" data-testid="nav-why-pocketbima">Why PocketBima</Link>
          <NavLink to="/contact" className={linkCls}>Contact</NavLink>
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <a
            href={PHONE_TEL}
            className="flex items-center gap-2 rounded-full border border-navy/15 px-5 py-2.5 text-sm font-semibold text-navy transition-colors duration-200 hover:border-mint hover:text-mint-600"
            data-testid="header-call-button"
          >
            <Phone className="h-4 w-4" /> Call Us
          </a>
          <Link
            to="/contact"
            className="rounded-full bg-mint px-6 py-2.5 text-sm font-bold text-white shadow-mint transition-all duration-200 hover:-translate-y-0.5 hover:bg-mint-600"
            data-testid="header-quote-button"
          >
            Get a Quote
          </Link>
        </div>

        <button
          className="flex h-11 w-11 items-center justify-center rounded-xl border border-navy/10 text-navy lg:hidden"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-button"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="border-t border-navy/10 bg-white px-5 pb-6 pt-3 lg:hidden" data-testid="mobile-menu">
          {[{ label: "Home", to: "/" }, { label: "About Us", to: "/about" }, ...INSURANCE_MENU, { label: "Why PocketBima", to: "/#why" }, { label: "Contact", to: "/contact" }].map((l, i) => (
            <Link
              key={`${l.to}-${i}`}
              to={l.to}
              onClick={() => setOpen(false)}
              className="block border-b border-navy/5 py-3.5 text-base font-semibold text-navy"
              data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              {l.label}
            </Link>
          ))}
          <Link
            to="/contact"
            onClick={() => setOpen(false)}
            className="mt-4 block rounded-full bg-mint py-3.5 text-center text-base font-bold text-white"
            data-testid="mobile-quote-button"
          >
            Get a Quote
          </Link>
        </div>
      )}
    </header>
  );
}
